import type { QueryClient } from '@tanstack/react-query'
import type { Driver } from 'driver.js'
import type { TutorialFeatureFlags, TutorialScanPhase } from '../../lib'
import type { StateConfig } from '@/entities/state/model'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { driver } from 'driver.js'
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { orderKeys } from '@/entities/order/model'
import { stateKeys } from '@/entities/state/model'
import { storeKeys } from '@/entities/store/api'
import { OrderDetailsModal } from '@/features/order/ui/order-details/modal'
import {
  applyTutorialScanPhase,
  cleanupDriverElements,
  destroyDriverInstances,
  disableTutorialMode,
  enableTutorialMode,
  getClassicModalTutorialSteps,
  getDashboardTutorialSteps,
  getInitialsTutorialStep,
  getModalHandoffActiveIndex,
  getZebraModalTutorialSteps,
  getZebraPostModalDashboardSteps,
  injectTutorialHeaderStyle,
  isTutorialModeActive,
  resetTutorialDashboardScanProgress,
  setCurrentTutorialStep,
  syncTutorialMobileDashboardForStep,
  TUTORIAL_SCAN_ORDER_ID,
} from '../../lib'
import { CollectionCodeTutorialOverlay } from './CollectionCodeTutorialOverlay'
import { CompletionModal } from './CompletionModal'
import { InitialsTutorialOverlay } from './InitialsTutorialOverlay'
import { TutorialHeader } from './TutorialHeader'
import { TutorialLoadingModal } from './TutorialLoadingModal'

export interface DriverJsContextType {
  startTutorial: () => Promise<void>
  endTutorial: () => void
  isActive: boolean
  isStartingTutorial: boolean
  showTutorialModal: boolean
  tutorialOrderId: string | null
}

const DriverJsContext = createContext<DriverJsContextType | null>(null)

// Export context for use in hook
export { DriverJsContext }

interface DriverJsProviderProps {
  children: React.ReactNode
}

const TUTORIAL_HEADER_HEIGHT = 56
const TUTORIAL_HEADER_BUFFER = 8
/** Extra air between the tutorial header and the top of a spotlighted card (Steps 4/5/12/13). */
const TUTORIAL_TARGET_TOP_CLEARANCE = 16

const ZEBRA_MODAL_PHASES: TutorialScanPhase[] = [
  'order-crates',
  'scanned-crates',
  'missing-sheet',
  'missing-success',
  'all-accounted',
  'order-details-status',
]

injectTutorialHeaderStyle(TUTORIAL_HEADER_HEIGHT)

/**
 * Drop every tutorial mock from the shared query cache. Reset (not just invalidate) so a failed
 * or slow refetch can never leave mock orders on the real dashboard; active queries refetch real data.
 */
function purgeTutorialQueryCache(queryClient: QueryClient) {
  const keys = [orderKeys.orders(), orderKeys.waitTime(), stateKeys.stateConfig(), storeKeys.details()]
  for (const queryKey of keys) {
    void queryClient.resetQueries({ queryKey })
  }
}

function readTutorialFlags(stateConfig: StateConfig | undefined): TutorialFeatureFlags {
  return {
    zebraBarcodeEnabled: stateConfig?.features?.zebraBarcodeEnabled ?? false,
    collectTMInitialsEnabled: stateConfig?.features?.collectTMInitialsEnabled ?? false,
    collectCodeEnabled: stateConfig?.features?.collectCodeEnabled ?? false,
  }
}

/** Top edge of the visible mobile bottom sheet (not an inner form row). */
function getMobileSheetTop(fallbackTarget: Element): number {
  const drawerSelectors = [
    '.mantine-Drawer-content[data-onboarding="tutorial-missing-crates-modal"]',
    '.mantine-Drawer-content[data-onboarding="tutorial-initials-modal"]',
    '.mantine-Drawer-content:has([data-onboarding="tutorial-missing-crates-sheet"])',
    '.mantine-Drawer-content:has([data-onboarding="tutorial-missing-crates-success"])',
    '.mantine-Drawer-content:has([data-onboarding="tutorial-initials-form"])',
  ]
  for (const selector of drawerSelectors) {
    const el = document.querySelector(selector)
    if (!el) {
      continue
    }
    const top = el.getBoundingClientRect().top
    if (top > TUTORIAL_HEADER_HEIGHT) {
      return top
    }
  }
  return fallbackTarget.getBoundingClientRect().top
}

function isMobileSheetSpotlight(el: Element | null): boolean {
  if (!el) {
    return false
  }
  return Boolean(
    el.closest('[data-onboarding="tutorial-missing-crates-modal"]')
    || el.matches('[data-onboarding="tutorial-missing-crates-modal"]')
    || el.matches('[data-onboarding="tutorial-missing-crates-sheet"]')
    || el.matches('[data-onboarding="tutorial-missing-crates-success"]')
    || el.closest('[data-onboarding="tutorial-initials-modal"]')
    || el.matches('[data-onboarding="tutorial-initials-modal"]')
    || el.matches('[data-onboarding="tutorial-initials-form"]'),
  )
}

function shouldParkTipAboveOnMobile(el: Element | null): boolean {
  if (!el) {
    return false
  }
  return el.matches('[data-onboarding="update-status-button"]') || isMobileSheetSpotlight(el)
}

function shouldParkTipBelowOnMobile(el: Element | null): boolean {
  if (!el) {
    return false
  }
  return Boolean(
    el.matches('[data-onboarding="order-card-scan-button"]')
    || el.matches('[data-onboarding="order-card-action-button"]')
    || el.matches('[data-onboarding="group-order-card"]')
    || el.closest('[data-onboarding="group-order-card"]'),
  )
}

/**
 * Driver.js calls ae() AFTER onPopoverRender and again on refresh() — those overwrite
 * plain style assigns, so we re-apply with !important + a short MutationObserver.
 */
function parkPopoverJustAboveTarget(wrapper: HTMLElement, target: Element, gap = 12) {
  wrapper.classList.add('driver-popover-park-above')

  // Missing Crates sheet (Step 8): a few extra px so Next is not clipped on TC72,
  // taken from a tighter gap + smaller highlight rather than covering the sheet.
  // place() and the observer below must agree on this gap, or the observer re-places forever.
  const gapFor = () => (isMobileSheetSpotlight(target) ? 8 : gap)

  // WebKit queues a style mutation even when the value is unchanged — skip no-op writes
  // so place() cannot re-trigger its own observer (froze iOS on Step 7 → 8).
  const setImportant = (prop: string, value: string) => {
    if (wrapper.style.getPropertyValue(prop) === value && wrapper.style.getPropertyPriority(prop) === 'important') {
      return
    }
    wrapper.style.setProperty(prop, value, 'important')
  }

  let placing = false
  const place = () => {
    if (placing || !wrapper.isConnected) {
      return
    }
    placing = true
    try {
      const spotTop = getMobileSheetTop(target)
      if (spotTop <= 0) {
        return
      }
      const minTop = TUTORIAL_HEADER_HEIGHT + TUTORIAL_HEADER_BUFFER
      const sheet = isMobileSheetSpotlight(target)
      const gapUsed = gapFor()
      const extraHeight = sheet ? 10 : 0
      const available = Math.max(96, spotTop - minTop - gapUsed + extraHeight)

      setImportant('transition', 'none')
      setImportant('transform', 'none')
      setImportant('max-height', `${available}px`)
      setImportant('overflow', 'visible')
      setImportant('overflow-y', 'visible')
      // Force layout so height reflects max-height before we read it.
      void wrapper.offsetHeight

      const rect = wrapper.getBoundingClientRect()
      const pw = rect.width || wrapper.offsetWidth
      const ph = rect.height || wrapper.offsetHeight
      if (pw <= 0 || ph <= 0) {
        return
      }

      const left = Math.max(8, Math.min(
        (window.innerWidth - pw) / 2,
        window.innerWidth - pw - 8,
      ))
      // Hard rule: tip bottom stays above the sheet top.
      const top = Math.max(minTop, spotTop - ph - gapUsed)

      setImportant('left', `${left}px`)
      setImportant('right', 'auto')
      setImportant('bottom', 'auto')
      setImportant('top', `${top}px`)
      setImportant('transform', 'none')

      // If tip still intersects the sheet (e.g. height ignored max-height), shrink once more.
      const after = wrapper.getBoundingClientRect()
      if (after.bottom > spotTop - gapUsed + 1) {
        const tighter = Math.max(80, spotTop - minTop - gapUsed + extraHeight)
        setImportant('max-height', `${tighter}px`)
        void wrapper.offsetHeight
        const ph2 = wrapper.getBoundingClientRect().height
        setImportant('top', `${Math.max(minTop, spotTop - ph2 - gapUsed)}px`)
      }
    } finally {
      placing = false
    }
  }

  place()

  const obs = new MutationObserver(() => {
    const spotTop = getMobileSheetTop(target)
    const after = wrapper.getBoundingClientRect()
    if (after.bottom > spotTop - gapFor() + 2 || after.top < minTopAllowed()) {
      place()
    }
  })
  obs.observe(wrapper, { attributes: true, attributeFilter: ['style', 'class'] })

  const timers = [0, 16, 48, 100, 200, 350, 500].map(ms => window.setTimeout(place, ms))
  window.setTimeout(() => {
    obs.disconnect()
    timers.forEach(id => window.clearTimeout(id))
  }, 600)
}

function minTopAllowed() {
  return TUTORIAL_HEADER_HEIGHT + TUTORIAL_HEADER_BUFFER
}

function tutorialHeaderBottom() {
  const header = document.querySelector('[data-tutorial-header="true"]')
  if (header) {
    return Math.ceil(header.getBoundingClientRect().bottom) + TUTORIAL_HEADER_BUFFER
  }
  return TUTORIAL_HEADER_HEIGHT + TUTORIAL_HEADER_BUFFER
}

/** Scroll the spotlight fully below the tutorial header so the cutout is not tucked under it. */
function revealTargetBelowTutorialHeader(target: Element) {
  const minTop = tutorialHeaderBottom() + TUTORIAL_TARGET_TOP_CLEARANCE
  const before = target.getBoundingClientRect().top
  // 1px slack: card tops are fractional (e.g. 79.8 vs 80) and scrollTop is whole pixels, so a
  // strict check re-scrolls every time without ever getting there.
  if (before >= minTop - 1) {
    return false
  }

  const card = (target.closest('[data-onboarding="order-card"]') as HTMLElement | null)
    || (target as HTMLElement)
  card.style.scrollMarginTop = `${minTop}px`
  card.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' })

  const afterScroll = target.getBoundingClientRect().top
  if (afterScroll < minTop) {
    applyPageScrollDelta(afterScroll - minTop)
  }

  return target.getBoundingClientRect().top !== before
}

function waitForTutorialCondition(ready: () => boolean, maxAttempts = 40, interval = 50) {
  return new Promise<void>((resolve) => {
    let attempts = 0
    const tick = () => {
      if (ready() || attempts >= maxAttempts) {
        resolve()
        return
      }
      attempts += 1
      window.setTimeout(tick, interval)
    }
    tick()
  })
}

function popoverOverlapsTarget(popover: DOMRect, target: DOMRect, gap: number) {
  return !(
    popover.right + gap <= target.left
    || popover.left >= target.right + gap
    || popover.bottom + gap <= target.top
    || popover.top >= target.bottom + gap
  )
}

function isScrollContainer(el: Element) {
  if (el.scrollHeight <= el.clientHeight + 1) {
    return false
  }
  const overflowY = getComputedStyle(el).overflowY
  return overflowY === 'auto' || overflowY === 'scroll'
}

/**
 * Scroll by `delta` exactly once per scroll container. The page scroller used to be hit three
 * times (scrollTo + documentElement + the ancestor walk), overshooting by 3× — Step 5 on Zebra
 * ended with the Partner card mid-screen, the tip on top of it and the spotlight out of sync.
 */
function applyPageScrollDelta(delta: number) {
  const scrollers = new Set<Element>([document.scrollingElement || document.documentElement])
  document.querySelectorAll<HTMLElement>(
    '.mantine-AppShell-root, .mantine-AppShell-main, .mantine-ScrollArea-viewport, [data-dashboard-scroll]',
  ).forEach((el) => {
    if (isScrollContainer(el)) {
      scrollers.add(el)
    }
  })
  let node: HTMLElement | null = document.body
  while (node) {
    if (isScrollContainer(node)) {
      scrollers.add(node)
    }
    node = node.parentElement
  }
  scrollers.forEach((el) => {
    el.scrollTop += delta
  })
}

/** Scroll the order card up so the full-size tip fits below the highlight on screen. */
function scrollTargetToMakeRoomBelow(target: Element, popoverHeight: number, gap: number) {
  const minTop = tutorialHeaderBottom() + TUTORIAL_TARGET_TOP_CLEARANCE
  const bottomInset = 40
  const neededTargetBottom = window.innerHeight - popoverHeight - gap - bottomInset
  const rect = target.getBoundingClientRect()
  if (rect.bottom <= neededTargetBottom + 1) {
    return false
  }

  const card = (target.closest('[data-onboarding="order-card"]') as HTMLElement | null)
    || (target.closest('[data-onboarding="group-order-card"]') as HTMLElement | null)
    || (target as HTMLElement)

  card.style.scrollMarginTop = `${minTop}px`
  card.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' })

  const after = target.getBoundingClientRect()
  // Never trade the top of the card for room below: the card now sits just under the header, so
  // scrolling further would tuck its heading under it (Steps 5/13). The tip compacts or moves instead.
  const maxExtraScroll = Math.max(0, card.getBoundingClientRect().top - minTop)
  const stillNeed = Math.min(after.bottom - neededTargetBottom, maxExtraScroll)
  if (stillNeed > 2) {
    applyPageScrollDelta(stillNeed)
  }
  const moved = rect.bottom - target.getBoundingClientRect().bottom
  // Re-place only if the card actually moved up; otherwise stop so we don't loop.
  return moved > 2
}

/**
 * Pin tip just below the spotlight and keep it there.
 * Driver.js ae()/refresh() otherwise flips Steps 12–13 onto the left of the card.
 */
function parkPopoverJustBelowTarget(
  wrapper: HTMLElement,
  target: Element,
  gap = 16,
  align: 'center' | 'target' = 'center',
) {
  wrapper.classList.add('driver-popover-park-below')

  let placing = false
  let scrollTries = 0
  let resyncFrame = 0
  const isCurrentStep = () =>
    wrapper.isConnected && target.isConnected && target.classList.contains('driver-active-element')

  // Driver.js refresh() redraws the spotlight on its NEXT frame and re-positions the tip while
  // doing so — re-park one frame after that, or the tip keeps Driver.js's (overlapping) spot.
  const resync = (rePark: () => void) => {
    if (resyncFrame) {
      return
    }
    resyncFrame = requestAnimationFrame(() => {
      resyncFrame = 0
      if (!isCurrentStep()) {
        return
      }
      ;(window as Window & { driverInstance?: { refresh?: () => void } }).driverInstance?.refresh?.()
      requestAnimationFrame(rePark)
    })
  }

  const place = () => {
    if (placing || !wrapper.isConnected) {
      return
    }
    placing = true
    try {
      // Never leave the top of the spotlight tucked under (or scrolled past) the tutorial header.
      if (revealTargetBelowTutorialHeader(target)) {
        resync(place)
      }

      wrapper.style.setProperty('transition', 'none', 'important')
      wrapper.style.setProperty('transform', 'none', 'important')
      wrapper.style.setProperty('max-height', 'none', 'important')
      wrapper.style.setProperty('height', 'auto', 'important')
      wrapper.style.setProperty('overflow', 'visible', 'important')
      wrapper.style.setProperty('justify-content', 'flex-start', 'important')
      void wrapper.offsetHeight

      const rect = wrapper.getBoundingClientRect()
      const pw = rect.width || wrapper.offsetWidth
      const ph = rect.height || wrapper.offsetHeight
      if (pw <= 0 || ph <= 0) {
        return
      }

      if (scrollTries < 4 && scrollTargetToMakeRoomBelow(target, ph, gap)) {
        scrollTries += 1
        resync(place)
        return
      }

      const targetRect = target.getBoundingClientRect()
      const spotBottom = targetRect.bottom
      if (spotBottom <= 0) {
        return
      }

      const vw = window.innerWidth
      const vh = window.innerHeight
      const bottomInset = 8
      const minTop = tutorialHeaderBottom()
      const centered = Math.max(8, Math.min((vw - pw) / 2, vw - pw - 8))
      const besideTarget = Math.max(8, Math.min(targetRect.left, vw - pw - 8))
      const left = align === 'target' ? besideTarget : centered

      // Short screens (Zebra TC52/TC72): the full tip can be taller than the room below the
      // spotlight, which pushes Next off-screen. Compact the tip, then fall back above / clamp.
      const spaceBelow = vh - spotBottom - gap - bottomInset
      let tipHeight = ph
      if (tipHeight > spaceBelow) {
        wrapper.classList.add('driver-popover-compact')
        void wrapper.offsetHeight
        tipHeight = wrapper.getBoundingClientRect().height || ph
      }
      let top = spotBottom + gap
      if (tipHeight > spaceBelow) {
        const spaceAbove = targetRect.top - minTop - gap
        top = spaceAbove > spaceBelow
          ? Math.max(minTop, targetRect.top - tipHeight - gap)
          : Math.max(minTop, vh - tipHeight - bottomInset)
      }

      wrapper.style.setProperty('left', `${left}px`, 'important')
      wrapper.style.setProperty('right', 'auto', 'important')
      wrapper.style.setProperty('bottom', 'auto', 'important')
      wrapper.style.setProperty('top', `${top}px`, 'important')
      wrapper.style.setProperty('transform', 'none', 'important')
    } finally {
      placing = false
    }
  }

  place()

  const obs = new MutationObserver(() => {
    const after = wrapper.getBoundingClientRect()
    const minTop = tutorialHeaderBottom()
    const vh = window.innerHeight
    if (after.top < minTop - 1 || after.bottom > vh - 8) {
      place()
    }
  })
  obs.observe(wrapper, { attributes: true, attributeFilter: ['style', 'class'] })

  const timers = [0, 16, 48, 100, 200, 350, 500, 800, 1200].map(ms => window.setTimeout(place, ms))
  window.setTimeout(() => {
    obs.disconnect()
    timers.forEach(id => window.clearTimeout(id))
  }, 1400)

  // Any later scroll (a slow WebView applying ours late, a swipe, an inner scroller Driver.js
  // does not listen to) must move the spotlight and tip together — Zebra Step 5 was left with
  // the spotlight on the empty Ready to collect strip and the Partner card under the header.
  // Only when the spotlight target really moved — scrollIntoView fires scroll even when nothing
  // changes, and a resync every frame keeps cancelling Driver.js's own redraw.
  let lastTop = target.getBoundingClientRect().top
  const onScroll = () => {
    if (!isCurrentStep()) {
      document.removeEventListener('scroll', onScroll, true)
      return
    }
    const top = target.getBoundingClientRect().top
    if (Math.abs(top - lastTop) < 1) {
      return
    }
    lastTop = top
    resync(place)
  }
  document.addEventListener('scroll', onScroll, { capture: true, passive: true })
}

/**
 * Desktop Steps 12–13: pin tip to the left of the spotlight at full size.
 * Do not cap height — shrinking below the card was clipping the copy.
 */
function parkPopoverToLeftOfTarget(wrapper: HTMLElement, target: Element, gap = 16) {
  wrapper.classList.add('driver-popover-park-left')
  wrapper.classList.remove('driver-popover-park-below')

  let placing = false
  const place = () => {
    if (placing || !wrapper.isConnected) {
      return
    }
    placing = true
    try {
      const targetRect = target.getBoundingClientRect()
      const minTop = tutorialHeaderBottom()
      const vw = window.innerWidth
      const vh = window.innerHeight

      wrapper.style.setProperty('transition', 'none', 'important')
      wrapper.style.setProperty('transform', 'none', 'important')
      wrapper.style.setProperty('max-height', 'none', 'important')
      wrapper.style.setProperty('height', 'auto', 'important')
      wrapper.style.setProperty('overflow', 'hidden', 'important')
      void wrapper.offsetHeight

      const rect = wrapper.getBoundingClientRect()
      const pw = rect.width || wrapper.offsetWidth || 300
      const ph = rect.height || wrapper.offsetHeight || 200
      if (pw <= 0 || ph <= 0) {
        return
      }

      const left = Math.max(8, Math.min(targetRect.left - pw - gap, vw - pw - 8))
      let top = Math.max(minTop, targetRect.top)
      if (top + ph > vh - 8) {
        top = Math.max(minTop, vh - ph - 8)
      }

      wrapper.style.setProperty('left', `${left}px`, 'important')
      wrapper.style.setProperty('right', 'auto', 'important')
      wrapper.style.setProperty('bottom', 'auto', 'important')
      wrapper.style.setProperty('top', `${top}px`, 'important')
    } finally {
      placing = false
    }
  }

  place()

  const obs = new MutationObserver(() => {
    const targetRect = target.getBoundingClientRect()
    const after = wrapper.getBoundingClientRect()
    const minTop = tutorialHeaderBottom()
    const expectedLeft = Math.max(8, targetRect.left - after.width - gap)
    if (
      after.top < minTop - 1
      || Math.abs(after.left - expectedLeft) > 8
      || popoverOverlapsTarget(after, targetRect, gap)
    ) {
      place()
    }
  })
  obs.observe(wrapper, { attributes: true, attributeFilter: ['style', 'class'] })

  const timers = [0, 16, 48, 100, 200, 350, 500, 800, 1200].map(ms => window.setTimeout(place, ms))
  window.setTimeout(() => {
    obs.disconnect()
    timers.forEach(id => window.clearTimeout(id))
  }, 1400)
}

/** Clear leftover scroll from later tutorial steps so Step 1 tip stays aligned on Play again. */
function resetTutorialScroll() {
  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
  document.querySelectorAll<HTMLElement>(
    '.mantine-AppShell-main, .mantine-AppShell-navbar, .mantine-ScrollArea-viewport, [data-dashboard-scroll]',
  ).forEach((el) => {
    el.scrollTop = 0
    el.scrollLeft = 0
  })
}

export function DriverJsProvider({ children }: DriverJsProviderProps) {
  const [isActive, setIsActive] = useState(false)
  const [isStartingTutorial, setIsStartingTutorial] = useState(false)
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [showTutorialModal, setShowTutorialModal] = useState(false)
  const [showTutorialCollectionCode, setShowTutorialCollectionCode] = useState(false)
  const [showTutorialInitials, setShowTutorialInitials] = useState(false)
  const [tutorialOrderId, setTutorialOrderId] = useState<string | null>(null)
  const [tutorialScanPhase, setTutorialScanPhase] = useState<TutorialScanPhase | null>(null)
  const driverRef = useRef<Driver | null>(null)
  const activeDriverRef = useRef<Driver | null>(null)
  const isTransitioningDriverRef = useRef(false)
  const zebraModalStepIndexRef = useRef(0)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  useEffect(() => {
    // Load CSS only in browser (not Node.js/Playwright)
    if (typeof window !== 'undefined') {
      import('./tutorial-styles.css').catch((error) => {
        console.error('Failed to load tutorial-styles.css:', error)
      })
      import('./TutorialHeader.css').catch((error) => {
        console.error('Failed to load TutorialHeader.css:', error)
      })
      import('./TutorialModal.css').catch((error) => {
        console.error('Failed to load TutorialModal.css:', error)
      })
    }
  }, [])

  const isOnboardingDisabled = import.meta.env.VITE_DISABLE_ONBOARDING === 'true'

  const resetTutorialState = useCallback(async () => {
    setIsActive(false)
    setIsStartingTutorial(false)
    setCurrentTutorialStep(0)
    setShowTutorialModal(false)
    setShowTutorialCollectionCode(false)
    setShowTutorialInitials(false)
    setTutorialOrderId(null)
    setTutorialScanPhase(null)
  }, [])

  const handleTutorialExit = useCallback(async () => {
    try {
      const success = await disableTutorialMode()

      purgeTutorialQueryCache(queryClient)

      if (success) {
        navigate({
          to: '/dashboard',
          search: '',
          replace: true,
          resetScroll: true,
        })
      }
    } catch (error) {
      console.error('Error disabling tutorial mode:', error)
      navigate({
        to: '/dashboard',
        search: '',
        replace: true,
        resetScroll: true,
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  // Simple cleanup function to avoid 'used before defined' issues
  const cleanupAndStop = useCallback(async () => {
    destroyDriverInstances(driverRef, activeDriverRef)
    resetTutorialState()
    cleanupDriverElements()
    await handleTutorialExit()
  }, [handleTutorialExit, resetTutorialState])

  const stylePopoverButtons = useCallback(() => {
    const nextButton = document.querySelector('.driver-popover-next-btn') as HTMLButtonElement
    const prevButton = document.querySelector('.driver-popover-prev-btn') as HTMLButtonElement
    if (nextButton) {
      nextButton.innerHTML = 'Next →'
      if (prevButton) {
        prevButton.style.display = 'none'
      }
    }
  }, [])

  const finishTutorial = useCallback(() => {
    setShowTutorialCollectionCode(false)
    setShowTutorialInitials(false)
    setShowTutorialModal(false)
    setTutorialOrderId(null)
    setTutorialScanPhase(null)
    setCurrentTutorialStep(0)
    setTimeout(() => {
      setShowCompletionModal(true)
    }, 100)
  }, [])

  const startInitialsDriver = useCallback((flags: TutorialFeatureFlags) => {
    setShowTutorialInitials(true)

    // Production initials use Modal/Drawer portals — wait briefly for the spotlight target.
    let attempts = 0
    const maxAttempts = 40
    const waitForInitialsForm = () => {
      const target = document.querySelector(
        '[data-onboarding="tutorial-initials-modal"], [data-onboarding="tutorial-initials-form"]',
      )
      if (!target && attempts < maxAttempts) {
        attempts += 1
        setTimeout(waitForInitialsForm, 50)
        return
      }
      if (!target) {
        finishTutorial()
        return
      }

      if (driverRef.current) {
        isTransitioningDriverRef.current = true
        driverRef.current.destroy()
        setTimeout(() => {
          isTransitioningDriverRef.current = false
        }, 0)
      }

      const isMobile = window.innerWidth <= 768
      const initialsDriver = driver({
        showProgress: false,
        showButtons: ['next', 'previous'],
        stagePadding: isMobile ? 4 : 0,
        animate: false,
        allowClose: false,
        steps: [getInitialsTutorialStep(flags, isMobile)],
        onNextClick: () => {
          isTransitioningDriverRef.current = true
          initialsDriver.destroy()
          setTimeout(() => {
            isTransitioningDriverRef.current = false
          }, 0)
          finishTutorial()
        },
        onPopoverRender: ({ wrapper }) => {
          stylePopoverButtons()
          if (!wrapper) {
            return
          }
          wrapper.style.zIndex = '99999'
          if (isMobile) {
            // Mobile Step 14: tip just above the full TMI sheet (re-park after driver ae()).
            requestAnimationFrame(() => {
              const active = document.querySelector('.driver-active-element')
              if (active) {
                parkPopoverJustAboveTarget(wrapper, active)
              }
            })
            return
          }
          // Desktop Step 14: compact tip to the right of initials (no overlap / no extra height).
          wrapper.classList.add('driver-popover-initials-tip')
          requestAnimationFrame(() => {
            const active = document.querySelector('.driver-active-element')
            if (!active) {
              return
            }
            const targetRect = active.getBoundingClientRect()
            const popoverRect = wrapper.getBoundingClientRect()
            const gap = 24
            const pw = popoverRect.width
            const ph = popoverRect.height
            const spaceLeft = targetRect.left - gap
            const spaceRight = window.innerWidth - targetRect.right - gap
            const left = spaceRight >= pw || spaceRight >= spaceLeft
              ? Math.min(targetRect.right + gap, window.innerWidth - pw - 8)
              : Math.max(8, targetRect.left - pw - gap)
            // Top-align with the highlight so a shorter tip does not sit over its top edge.
            const top = Math.max(
              TUTORIAL_HEADER_HEIGHT + TUTORIAL_HEADER_BUFFER,
              Math.min(targetRect.top, window.innerHeight - ph - 8),
            )
            wrapper.style.left = `${left}px`
            wrapper.style.top = `${top}px`
          })
        },
        onHighlighted: () => {
          if (!isMobile) {
            return
          }
          const tip = document.querySelector('.driver-popover') as HTMLElement | null
          const active = document.querySelector('.driver-active-element')
          if (tip && active) {
            parkPopoverJustAboveTarget(tip, active)
          }
        },
        onHighlightStarted: () => {
          setCurrentTutorialStep(flags.zebraBarcodeEnabled ? 14 : (flags.collectCodeEnabled ? 10 : 9))
        },
        onDestroyStarted: () => {
          if (isTransitioningDriverRef.current) {
            return
          }
          cleanupAndStop()
        },
        onCloseClick: () => cleanupAndStop(),
      })

      driverRef.current = initialsDriver
      activeDriverRef.current = initialsDriver
      initialsDriver.drive()
    }

    setTimeout(waitForInitialsForm, 50)
  }, [cleanupAndStop, finishTutorial, stylePopoverButtons])

  const startPostZebraDashboard = useCallback((flags: TutorialFeatureFlags) => {
    const isMobile = window.innerWidth <= 768
    setShowTutorialModal(false)
    setTutorialOrderId(null)
    setTutorialScanPhase(null)
    resetTutorialScroll()

    setTimeout(() => {
      if (driverRef.current) {
        isTransitioningDriverRef.current = true
        driverRef.current.destroy()
        setTimeout(() => {
          isTransitioningDriverRef.current = false
        }, 0)
      }

      const postDriver = driver({
        showProgress: false,
        showButtons: ['next', 'previous', 'close'],
        steps: getZebraPostModalDashboardSteps(flags, isMobile),
        // Instant scroll so the Step 13 cutout/tip match the card rect on every width.
        smoothScroll: false,
        // Mobile: room for the button's green border / card edge so the cutout does not clip them.
        stagePadding: isMobile ? 10 : 2,
        animate: false,
        allowClose: true,
        onNextClick: (_el, _step, { state }) => {
          const activeIndex = state?.activeIndex || 0
          const isLast = activeIndex >= getZebraPostModalDashboardSteps(flags, isMobile).length - 1
          if (isLast) {
            isTransitioningDriverRef.current = true
            postDriver.destroy()
            setTimeout(() => {
              isTransitioningDriverRef.current = false
            }, 0)
            if (flags.collectTMInitialsEnabled) {
              startInitialsDriver(flags)
            } else {
              finishTutorial()
            }
            return
          }
          postDriver.moveNext()
        },
        onPopoverRender: ({ wrapper }) => {
          stylePopoverButtons()
          if (!wrapper) {
            return
          }
          wrapper.style.zIndex = '99999'
          requestAnimationFrame(() => {
            const target = document.querySelector('.driver-active-element')
            if (!target) {
              return
            }
            if (isMobile) {
              parkPopoverJustBelowTarget(wrapper, target)
              return
            }
            parkPopoverToLeftOfTarget(wrapper, target)
          })
        },
        onHighlightStarted: (_el, _step, options) => {
          setCurrentTutorialStep(12 + (options.state?.activeIndex || 0))
          const target = (_el as Element | undefined)
            || document.querySelector('.driver-active-element')
          if (!target) {
            return
          }
          const scrolled = revealTargetBelowTutorialHeader(target)
          const park = () => {
            const wrapper = document.querySelector('.driver-popover') as HTMLElement | null
            const el = document.querySelector('.driver-active-element') || target
            if (!wrapper || !el) {
              return
            }
            if (isMobile) {
              parkPopoverJustBelowTarget(wrapper, el)
              return
            }
            parkPopoverToLeftOfTarget(wrapper, el)
          }
          if (scrolled || isMobile) {
            const relayout = () => {
              const el = document.querySelector('.driver-active-element') || target
              revealTargetBelowTutorialHeader(el)
              postDriver.refresh?.()
              park()
            }
            requestAnimationFrame(() => {
              postDriver.refresh?.()
              requestAnimationFrame(park)
            })
            if (isMobile) {
              window.setTimeout(relayout, 50)
              window.setTimeout(relayout, 160)
            }
            return
          }
          park()
        },
        onDestroyStarted: () => {
          if (isTransitioningDriverRef.current) {
            return
          }
          cleanupAndStop()
        },
        onCloseClick: () => cleanupAndStop(),
      })

      driverRef.current = postDriver
      activeDriverRef.current = postDriver
      window.driverInstance = postDriver
      postDriver.drive()
    }, 150)
  }, [cleanupAndStop, finishTutorial, startInitialsDriver, stylePopoverButtons])

  const openTutorialModalHandoff = useCallback((flags: TutorialFeatureFlags) => {
    const isMobile = window.innerWidth <= 768
    setTutorialOrderId(TUTORIAL_SCAN_ORDER_ID)
    setShowTutorialModal(true)

    if (flags.zebraBarcodeEnabled) {
      setShowTutorialCollectionCode(false)
      applyTutorialScanPhase(queryClient, 'order-crates')
      setTutorialScanPhase('order-crates')
    } else {
      setShowTutorialCollectionCode(flags.collectCodeEnabled)
      setTutorialScanPhase(null)
    }

    const waitForModalAndHighlight = () => {
      setTimeout(() => {
        let attempts = 0
        const maxAttempts = 30

        const findAndHighlight = () => {
          attempts++
          const zebra = flags.zebraBarcodeEnabled
          // Zebra Step 6: wait for Scan crates CTA + crate list (Total crates 0/N), not just the panel shell.
          const scanCratesButton = document.querySelector('[data-onboarding="update-status-button"]')
          const crateProgress = document.querySelector('[data-onboarding="tutorial-crate-progress"]')
          const crateRowsReady = Boolean(
            crateProgress
            && crateProgress.textContent
            && /Total crates/i.test(crateProgress.textContent)
            && !/0\/0/.test(crateProgress.textContent),
          )
          const orderDetailsShell = document.querySelector('[data-onboarding="order-details-modal"]')
          const orderDetailsTarget = document.querySelector('[data-onboarding="order-details-modal-content"]')
            || crateProgress
            || orderDetailsShell
          // Step 6: wait on Scan crates CTA (+ crate rows ready so the list is visible behind the mask).
          const targetElement = zebra
            ? (isMobile
                ? (crateRowsReady && scanCratesButton ? scanCratesButton : null)
                : (scanCratesButton && crateRowsReady ? scanCratesButton : null)
                  || orderDetailsTarget)
            : flags.collectCodeEnabled
              ? document.querySelector('[data-onboarding="tutorial-collection-code-card"]')
              : (orderDetailsTarget
                || document.querySelector('[data-onboarding="update-status-button"]'))

          const zebraReady = Boolean(
            crateRowsReady && scanCratesButton,
          )

          if (targetElement && (zebra ? zebraReady : orderDetailsTarget)) {
            if (driverRef.current) {
              isTransitioningDriverRef.current = true
              driverRef.current.destroy()
              setTimeout(() => {
                isTransitioningDriverRef.current = false
              }, 0)
            }

            const modalSteps = zebra
              ? getZebraModalTutorialSteps(flags, isMobile)
              : getClassicModalTutorialSteps(flags, isMobile)

            // Step 7 needs a tight cutout around one crate row (avoid bleeding into the next).
            const modalDriver = driver({
              showProgress: false,
              showButtons: ['next', 'previous'],
              // Mobile: 0 so the Missing Crates ring does not eat the Step 8 Next button.
              stagePadding: zebra && !isMobile ? 2 : 0,
              animate: false,
              allowClose: false,
              steps: modalSteps,
              onNextClick: (_activeElement, _activeStep, options) => {
                const modalStepIndex = options.state?.activeIndex || 0

                if (!zebra) {
                  const isCollectionCodeStep = flags.collectCodeEnabled && modalStepIndex === 0
                  if (isCollectionCodeStep) {
                    setShowTutorialCollectionCode(false)
                    setTimeout(() => {
                      modalDriver.moveNext()
                    }, 100)
                    return
                  }

                  isTransitioningDriverRef.current = true
                  modalDriver.destroy()
                  setTimeout(() => {
                    isTransitioningDriverRef.current = false
                  }, 0)

                  if (flags.collectTMInitialsEnabled) {
                    setShowTutorialModal(false)
                    setTutorialOrderId(null)
                    startInitialsDriver(flags)
                  } else {
                    finishTutorial()
                  }
                  return
                }

                // Zebra modal progression
                const isLastModalStep = modalStepIndex >= ZEBRA_MODAL_PHASES.length - 1
                if (isLastModalStep) {
                  isTransitioningDriverRef.current = true
                  modalDriver.destroy()
                  setTimeout(() => {
                    isTransitioningDriverRef.current = false
                  }, 0)
                  startPostZebraDashboard(flags)
                  return
                }

                const nextPhase = ZEBRA_MODAL_PHASES[modalStepIndex + 1]
                if (nextPhase) {
                  applyTutorialScanPhase(queryClient, nextPhase)
                  setTutorialScanPhase(nextPhase)
                }

                // Missing-sheet / success mount in a Mantine Drawer — wait until the
                // spotlight target exists so Driver.js does not fall back to a dummy.
                const waitSelector = nextPhase === 'missing-sheet' || nextPhase === 'missing-success'
                  ? '[data-onboarding="tutorial-missing-crates-modal"], [data-onboarding="tutorial-missing-crates-sheet"], [data-onboarding="tutorial-missing-crates-success"]'
                  : null

                const advance = () => {
                  modalDriver.moveNext()
                  requestAnimationFrame(() => {
                    modalDriver.refresh()
                    // refresh() re-runs driver ae() and overwrites tip position — re-park after.
                    if (!isMobile) {
                      return
                    }
                    requestAnimationFrame(() => {
                      const tip = document.querySelector('.driver-popover') as HTMLElement | null
                      const active = document.querySelector('.driver-active-element')
                      if (tip && shouldParkTipAboveOnMobile(active)) {
                        parkPopoverJustAboveTarget(tip, active!)
                      }
                    })
                  })
                }

                if (!waitSelector) {
                  setTimeout(advance, 120)
                  return
                }

                let attempts = 0
                const maxAttempts = 40
                const waitForTarget = () => {
                  attempts++
                  if (document.querySelector(waitSelector) || attempts >= maxAttempts) {
                    advance()
                    return
                  }
                  setTimeout(waitForTarget, 50)
                }
                setTimeout(waitForTarget, 50)
              },
              onPopoverRender: ({ wrapper }) => {
                stylePopoverButtons()
                if (!wrapper) {
                  return
                }
                wrapper.style.zIndex = '99999'
                const modalIdx = zebraModalStepIndexRef.current
                // Steps 8–10 desktop zebra: park tip left/right of the spotlight (right preferred).
                const parkTipBeside = zebra && !isMobile
                  && (modalIdx === 2 || modalIdx === 3 || modalIdx === 4)
                // Classic desktop Order Details (Step 9): compact tip beside the panel (no overlap).
                const parkClassicOrderDetails = !zebra && !isMobile
                // onPopoverRender runs BEFORE driver ae() — schedule park after ae + refresh.
                requestAnimationFrame(() => {
                  const target = document.querySelector('.driver-active-element')
                  if (!target) {
                    return
                  }
                  if (zebra && isMobile && shouldParkTipAboveOnMobile(target)) {
                    parkPopoverJustAboveTarget(wrapper, target)
                    return
                  }

                  if (parkClassicOrderDetails) {
                    wrapper.classList.add('driver-popover-classic-order-details-tip')
                    // Collapse any prior top+bottom stretch from Driver.js positioning.
                    wrapper.style.setProperty('bottom', 'auto', 'important')
                    wrapper.style.setProperty('height', 'fit-content', 'important')
                    wrapper.style.setProperty('min-height', '0', 'important')
                    wrapper.style.setProperty('max-height', 'none', 'important')
                    void wrapper.offsetHeight
                  }

                  const targetRect = target.getBoundingClientRect()
                  const popoverRect = wrapper.getBoundingClientRect()
                  const gap = 16
                  const viewportWidth = window.innerWidth
                  const viewportHeight = window.innerHeight
                  const minTop = TUTORIAL_HEADER_HEIGHT + TUTORIAL_HEADER_BUFFER
                  let left = Number.parseFloat(wrapper.style.left || `${popoverRect.left}`)
                  let top = Number.parseFloat(wrapper.style.top || `${popoverRect.top}`)
                  const pw = popoverRect.width
                  const ph = popoverRect.height

                  if (parkClassicOrderDetails) {
                    // Prefer left; otherwise right with a clear gap (narrow tip fits beside the modal).
                    left = targetRect.left - gap >= pw
                      ? Math.max(8, targetRect.left - pw - gap)
                      : Math.min(targetRect.right + gap, viewportWidth - pw - 8)
                    // Top-align with the panel — height stays content-sized (no vertical stretch).
                    top = Math.max(
                      minTop,
                      Math.min(targetRect.top, viewportHeight - ph - 8),
                    )
                    wrapper.style.setProperty('left', `${left}px`, 'important')
                    wrapper.style.setProperty('top', `${top}px`, 'important')
                    wrapper.style.setProperty('right', 'auto', 'important')
                    wrapper.style.setProperty('bottom', 'auto', 'important')
                    wrapper.style.setProperty('height', 'fit-content', 'important')
                    return
                  }

                  if (parkTipBeside) {
                    const spaceLeft = targetRect.left - gap
                    const spaceRight = viewportWidth - targetRect.right - gap
                    if (spaceRight >= pw || spaceRight >= spaceLeft) {
                      left = Math.min(targetRect.right + gap, viewportWidth - pw - 8)
                    } else {
                      left = Math.max(8, targetRect.left - pw - gap)
                    }
                    top = targetRect.top + (targetRect.height - ph) / 2
                    top = Math.max(minTop, Math.min(top, viewportHeight - ph - 8))
                    wrapper.style.left = `${left}px`
                    wrapper.style.top = `${top}px`
                    return
                  }

                  // Keep the popover clear of the highlighted stage (do not cover crate rows / CTAs).
                  const overlapsHorizontally = popoverRect.left < targetRect.right + gap
                    && popoverRect.right > targetRect.left - gap
                  const overlapsVertically = popoverRect.top < targetRect.bottom + gap
                    && popoverRect.bottom > targetRect.top - gap

                  if (overlapsHorizontally && overlapsVertically) {
                    const spaceLeft = targetRect.left - gap
                    const spaceRight = viewportWidth - targetRect.right - gap
                    if (spaceLeft >= popoverRect.width) {
                      left = targetRect.left - popoverRect.width - gap
                    } else if (spaceRight >= popoverRect.width) {
                      left = targetRect.right + gap
                    } else {
                      top = Math.min(targetRect.bottom + gap, viewportHeight - popoverRect.height - 16)
                    }
                  }

                  left = Math.max(8, Math.min(left, viewportWidth - popoverRect.width - 8))
                  top = Math.max(
                    minTop,
                    Math.min(top, viewportHeight - popoverRect.height - 8),
                  )
                  wrapper.style.left = `${left}px`
                  wrapper.style.top = `${top}px`
                })
              },
              onHighlighted: (_activeElement, _activeStep) => {
                if (!zebra || !isMobile) {
                  return
                }
                const tip = document.querySelector('.driver-popover') as HTMLElement | null
                const active = document.querySelector('.driver-active-element')
                if (tip && shouldParkTipAboveOnMobile(active)) {
                  parkPopoverJustAboveTarget(tip, active!)
                }
              },
              onHighlightStarted: (_activeElement, _activeStep, options) => {
                const modalStepIndex = options.state?.activeIndex || 0
                zebraModalStepIndexRef.current = modalStepIndex
                if (zebra) {
                  const phase = ZEBRA_MODAL_PHASES[modalStepIndex]
                  if (phase) {
                    applyTutorialScanPhase(queryClient, phase)
                    setTutorialScanPhase(phase)
                  }
                  setCurrentTutorialStep(6 + modalStepIndex)
                  return
                }

                const currentModalStep = flags.collectCodeEnabled
                  ? 8 + modalStepIndex
                  : 8
                setCurrentTutorialStep(currentModalStep)
              },
              onDestroyStarted: () => {
                if (isTransitioningDriverRef.current) {
                  return
                }
                cleanupAndStop()
              },
              onCloseClick: () => cleanupAndStop(),
            })

            driverRef.current = modalDriver
            activeDriverRef.current = modalDriver
            modalDriver.drive()
            return
          }

          if (attempts < maxAttempts) {
            setTimeout(findAndHighlight, 100)
          }
        }

        findAndHighlight()
      }, 100)
    }

    waitForModalAndHighlight()
  }, [cleanupAndStop, finishTutorial, queryClient, startInitialsDriver, startPostZebraDashboard, stylePopoverButtons])

  const startTutorial = useCallback(async () => {
    if (isOnboardingDisabled) {
      return
    }

    const liveConfig = queryClient.getQueryData<StateConfig>(stateKeys.stateConfig())
    const flags = readTutorialFlags(liveConfig)

    try {
      if (driverRef.current) {
        driverRef.current.destroy()
      }

      setIsStartingTutorial(true)

      const success = await enableTutorialMode(flags)

      if (!success) {
        setIsStartingTutorial(false)
        return
      }

      // Close any open order-details modal so in-memory scan state cannot re-write LS.
      navigate({
        to: '/dashboard',
        search: '',
        replace: true,
        resetScroll: true,
      })
      // Play again: later steps scrollIntoView — reset so Step 1 tip is not shifted up.
      resetTutorialScroll()

      queryClient.invalidateQueries()
      if (window.__tutorialData) {
        queryClient.setQueryData(orderKeys.orders(), window.__tutorialData.orders || [])
        queryClient.setQueryData(orderKeys.waitTime(), window.__tutorialData.waitTime || {})
        queryClient.setQueryData(stateKeys.stateConfig(), window.__tutorialData.stateConfig)
        queryClient.setQueryData(storeKeys.details(), window.__tutorialData.storeDetails || {})
      }
      // Modal phases / prior LS can leave 100001 as Scanned 7/7 — force teaching cards to 0/7.
      if (flags.zebraBarcodeEnabled) {
        resetTutorialDashboardScanProgress(queryClient)
      }

      await waitForOrderCards({ maxAttempts: 50, interval: 100 })

      async function waitForOrderCards({ maxAttempts = 50, interval = 100 } = {}) {
        for (let attempts = 0; attempts < maxAttempts; attempts++) {
          const headerActions = document.querySelector('[data-onboarding="header-actions"]')
          const rapidOrders = document.querySelector('[data-onboarding="rapid-orders-widget"]')
          const orderCards = document.querySelectorAll('[data-onboarding="order-card"]')
          const groupCards = document.querySelectorAll('[data-onboarding="group-order-card"]')
          if (headerActions && rapidOrders && orderCards.length >= 2 && groupCards.length >= 1) {
            return
          }
          await new Promise(resolve => setTimeout(resolve, interval))
        }
      }
    } catch (error) {
      console.error('Error enabling tutorial mode:', error)
      setIsStartingTutorial(false)
    }

    setCurrentTutorialStep(0)
    setIsActive(true)
    await waitForTutorialHeader()
    // After tutorial header mounts (body padding changes), scroll again before Step 1.
    resetTutorialScroll()

    async function waitForTutorialHeader({ maxAttempts = 30, interval = 50, settleDelay = 200 } = {}) {
      for (let attempts = 0; attempts < maxAttempts; attempts++) {
        const tutorialHeader = document.querySelector('[data-tutorial-header="true"]')
        const bodyHasClass = document.body.classList.contains('tutorial-header-active')
        if (tutorialHeader && bodyHasClass) {
          await new Promise(resolve => requestAnimationFrame(resolve))
          await new Promise(resolve => setTimeout(resolve, settleDelay))
          return
        }
        await new Promise(resolve => setTimeout(resolve, interval))
      }
    }

    const isMobile = window.innerWidth <= 768
    const handoffIndex = getModalHandoffActiveIndex(flags)

    const driverInstance = driver({
      showProgress: false,
      showButtons: ['next', 'previous', 'close'],
      steps: getDashboardTutorialSteps(flags, isMobile),
      // Instant scroll so the spotlight cutout matches the final element position.
      smoothScroll: false,
      stagePadding: 2,
      animate: false,
      allowClose: true,
      onNextClick: (_element, _step, { state }) => {
        const activeIndex = state?.activeIndex || 0

        if (activeIndex === handoffIndex) {
          if (isMobile) {
            syncTutorialMobileDashboardForStep(queryClient, handoffIndex + 1, true)
          }
          openTutorialModalHandoff(flags)
          return
        }

        // Slim/restore mock cards before Driver.js looks up the next target.
        if (isMobile && (activeIndex === 3 || activeIndex === 4 || activeIndex === 5)) {
          const nextIndex = activeIndex + 1
          syncTutorialMobileDashboardForStep(queryClient, nextIndex, true)
          // The next target is often already on the full board — wait until the slimmed /
          // restored board has rendered, or Driver.js measures the old layout and scroll.
          const boardReady = () => {
            const orderCards = document.querySelectorAll('[data-onboarding="order-card"]')
            if (nextIndex === 4) {
              return orderCards.length === 1
                && !!document.querySelector('[data-onboarding="section-on-the-way"] [data-onboarding="order-card"]')
            }
            if (nextIndex === 5) {
              return orderCards.length === 0 && !!document.querySelector('[data-onboarding="group-order-card"]')
            }
            return !!document.querySelector('[data-onboarding="section-arrived"] [data-onboarding="order-card"]')
          }
          void waitForTutorialCondition(boardReady).then(() => {
            driverRef.current?.moveNext()
          })
          return
        }

        driverRef.current?.moveNext()
      },
      onPopoverRender: ({ wrapper }) => {
        stylePopoverButtons()

        if (wrapper) {
          wrapper.style.zIndex = '99999'
          wrapper.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
          wrapper.style.opacity = '0'
          wrapper.style.transform = 'translateY(-10px)'

          void wrapper.offsetHeight

          requestAnimationFrame(() => {
            const minTopPosition = TUTORIAL_HEADER_HEIGHT + TUTORIAL_HEADER_BUFFER
            const viewportHeight = window.innerHeight
            const targetElement = document.querySelector('.driver-active-element')

            wrapper.style.display = 'block'
            const popoverRect = wrapper.getBoundingClientRect()

            // Mobile Steps 4–5: keep tip below the Scan / Partner highlight (do not pull up on overflow).
            if (isMobile && targetElement && shouldParkTipBelowOnMobile(targetElement)) {
              parkPopoverJustBelowTarget(wrapper, targetElement)
              setTimeout(() => {
                wrapper.style.opacity = '1'
                wrapper.style.transform = 'none'
              }, 50)
              return
            }

            let finalTopPosition = Number.parseInt(wrapper.style.top || '0')

            if (popoverRect.top < minTopPosition && popoverRect.top > -100) {
              if (targetElement) {
                const targetRect = targetElement.getBoundingClientRect()
                const spaceBelow = viewportHeight - (targetRect.bottom + 20)
                const spaceAbove = targetRect.top - minTopPosition

                if (spaceBelow > popoverRect.height && spaceBelow > spaceAbove) {
                  finalTopPosition = targetRect.bottom + 10
                } else {
                  const adjustment = minTopPosition - popoverRect.top
                  finalTopPosition += adjustment
                }
              } else {
                const adjustment = minTopPosition - popoverRect.top
                finalTopPosition += adjustment
              }
            }

            const tempRect = { ...popoverRect, top: finalTopPosition }
            if (tempRect.top + popoverRect.height > viewportHeight - 20) {
              const overflowAdjustment = (tempRect.top + popoverRect.height) - (viewportHeight - 20)
              finalTopPosition -= overflowAdjustment
            }

            if (finalTopPosition < 0) {
              finalTopPosition = minTopPosition
            }

            wrapper.style.top = `${finalTopPosition}px`

            setTimeout(() => {
              wrapper.style.opacity = '1'
              wrapper.style.transform = 'translateY(0)'
            }, 50)
          })
        }
      },
      onHighlightStarted: (_element, _step, options) => {
        const activeIndex = options.state?.activeIndex || 0
        setCurrentTutorialStep(activeIndex + 1)

        if (isMobile && syncTutorialMobileDashboardForStep(queryClient, activeIndex, true)) {
          requestAnimationFrame(() => {
            driverRef.current?.refresh()
          })
        }

        const targetElement = _element
        if (targetElement) {
          const rect = targetElement.getBoundingClientRect()

          if (rect.top < 0 || rect.bottom > window.innerHeight || rect.height < 2) {
            targetElement.scrollIntoView({ behavior: 'auto', block: 'center' })
            requestAnimationFrame(() => {
              driverRef.current?.refresh()
            })
          }
        }
      },
      onDestroyStarted: () => {
        if (isTransitioningDriverRef.current) {
          return
        }
        cleanupAndStop()
      },
      onCloseClick: () => {
        cleanupAndStop()
      },
    })

    driverRef.current = driverInstance
    activeDriverRef.current = driverInstance

    window.driverInstance = driverInstance

    setIsActive(true)
    setIsStartingTutorial(false)
    setCurrentTutorialStep(1)
    resetTutorialScroll()
    driverInstance.drive()
    // One more frame after drive so Step 1 spotlight/tip use scrollY === 0 rects.
    requestAnimationFrame(() => {
      resetTutorialScroll()
      driverInstance.refresh?.()
    })
  }, [queryClient, cleanupAndStop, isOnboardingDisabled, navigate, openTutorialModalHandoff, stylePopoverButtons])

  const endTutorial = useCallback(async () => {
    destroyDriverInstances(driverRef, activeDriverRef)
    resetTutorialState()
    cleanupDriverElements()
    await handleTutorialExit()
  }, [handleTutorialExit, resetTutorialState])

  const handleCompletionModalClose = useCallback(() => {
    setShowCompletionModal(false)
    endTutorial()
  }, [endTutorial])

  const handleRestartTutorial = useCallback(async () => {
    destroyDriverInstances(driverRef, activeDriverRef)

    const driverElements = document.querySelectorAll('.driver-popover, .driver-overlay, .driver-highlighted-element, .driver-no-interaction, .driver-fade-element, [data-driver-popover], [data-driver-overlay]')
    driverElements.forEach(element => element.remove())

    cleanupDriverElements()
    await resetTutorialState()
    await startTutorial()
    setShowCompletionModal(false)
  }, [startTutorial, resetTutorialState])

  const handlePlayAgain = useCallback(() => {
    setShowCompletionModal(false)

    if (driverRef.current) {
      driverRef.current.destroy()
      driverRef.current = null
    }

    cleanupDriverElements()
    resetTutorialScroll()

    const allElements = document.querySelectorAll('[data-onboarding]')
    allElements.forEach((element) => {
      (element as HTMLElement).style.transform = ''
      // Force reflow to ensure styles are applied
      void (element as HTMLElement).offsetHeight
    })

    resetTutorialState()

    setTimeout(() => {
      const remainingElements = document.querySelectorAll('.driver-popover, .driver-overlay, .driver-highlighted-element')
      if (remainingElements.length > 0) {
        remainingElements.forEach(el => el.remove())
      }

      resetTutorialScroll()
      handleRestartTutorial()
    }, 600)
  }, [resetTutorialState, handleRestartTutorial])

  // Leave the tutorial without navigating (route change, unmount): mock data must never outlive it.
  const abandonTutorial = useCallback(() => {
    destroyDriverInstances(driverRef, activeDriverRef)
    cleanupDriverElements()
    void disableTutorialMode()
    purgeTutorialQueryCache(queryClient)
    resetTutorialState()
  }, [queryClient, resetTutorialState])

  useEffect(() => {
    // Cleanup driverjs instances on unmount; if the tutorial was still on (logout, route change),
    // switch mock data off too so the real app never inherits it.
    return () => {
      destroyDriverInstances(driverRef, activeDriverRef)
      if (isTutorialModeActive()) {
        void disableTutorialMode()
        purgeTutorialQueryCache(queryClient)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The walkthrough only exists on /dashboard (its order-details modal is rendered in place).
  // Any other path (hardware back, deep link, menu) means the user left it: end it so the
  // history / overdue boards fetch real orders.
  useEffect(() => {
    if (isActive && pathname.replace(/\/$/, '') !== '/dashboard') {
      abandonTutorial()
    }
  }, [isActive, pathname, abandonTutorial])

  useEffect(() => {
    // Hide all notifications when tutorial starts
    if (isStartingTutorial) {
      notifications.clean()
    }
  }, [isStartingTutorial])

  const contextValue = useMemo(() => ({
    startTutorial,
    endTutorial,
    isActive,
    isStartingTutorial,
    showTutorialModal,
    tutorialOrderId,
  }), [startTutorial, endTutorial, isActive, isStartingTutorial, showTutorialModal, tutorialOrderId])

  return (
    <DriverJsContext value={contextValue}>
      {children}
      {isActive && <TutorialHeader onClose={endTutorial} />}
      {isStartingTutorial && <TutorialLoadingModal />}
      {showTutorialModal && tutorialOrderId && (
        <>
          <OrderDetailsModal
            orderId={tutorialOrderId}
            parentPath="/dashboard?onboarding=true"
            withoutBackdrop={true}
            tutorialScanPhase={tutorialScanPhase}
          />
          {showTutorialCollectionCode && <CollectionCodeTutorialOverlay />}
        </>
      )}
      {showTutorialInitials && <InitialsTutorialOverlay />}
      {showCompletionModal && (
        <CompletionModal
          onClose={handleCompletionModalClose}
          onPlayAgain={handlePlayAgain}
        />
      )}
    </DriverJsContext>
  )
}
